import type { DocumentReference } from 'fhir/r4';
import moment from 'moment';

import {
  getMemberDocumentFileName,
  getMemberDocumentUploadedBy,
  isMemberFileUpload,
} from '../documentReferenceQueries';

import type { MemberDocument } from './types';


/** Attachment fields needed to download a member document. */
export interface MemberDocumentAttachment {
  data: string;
  fileName: string;
  contentType?: string | undefined;
}

/**
 * Maps a FHIR DocumentReference to MemberDocument for table display.
 *
 * @remarks
 * List searches return `content.attachment.title` via nested `_elements`; download fetches the full resource.
 */
export function documentReferenceToMemberDocument(entry: { resource: DocumentReference }): MemberDocument | null {
  const resource = entry?.resource;
  if (!resource || !isMemberFileUpload(resource)) {
    return null;
  }

  const attachment = resource.content?.[0]?.attachment;
  const date = resource.date ?? resource.meta?.lastUpdated;

  return {
    id: resource.id ?? '',
    subject: resource.description?.trim() || 'Untitled',
    fileName: getMemberDocumentFileName(resource) || 'Untitled',
    lastUpdated: date ? moment.utc(date).local().format('YYYY-MM-DD hh:mm:ss a') : '',
    uploadedBy: getMemberDocumentUploadedBy(resource),
    attachmentData: attachment?.data,
    contentType: attachment?.contentType,
  };
}

/**
 * Reads attachment bytes from a full DocumentReference resource.
 */
export function getMemberDocumentAttachment(resource: DocumentReference): MemberDocumentAttachment | null {
  const attachment = resource.content?.[0]?.attachment;
  if (!attachment?.data) {
    return null;
  }

  const fileName = getMemberDocumentFileName(resource) || attachment.title?.trim() || 'document';

  return {
    data: attachment.data,
    fileName,
    contentType: attachment.contentType,
  };
}

/**
 * Filters DocumentReference bundle entries to only member documents (file uploads).
 */
export function filterMemberDocuments(entries: Array<{ resource: DocumentReference }>): MemberDocument[] {
  if (!entries?.length) return [];
  return entries.map(documentReferenceToMemberDocument).filter((doc): doc is MemberDocument => doc !== null);
}
